/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { CreateAuditConfigResponse2 } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type CreateAuditConfigParams = {
  configId: string;
  revisionNumber: number;
};

export type CreateAuditConfigMutationConfig = {
  onSuccess?: (data: CreateAuditConfigResponse2, variables: CreateAuditConfigParams) => void;
  onError?: (error: Error, variables: CreateAuditConfigParams) => void;
  onSettled?: (
    data: CreateAuditConfigResponse2 | undefined,
    error: Error | null,
    variables: CreateAuditConfigParams,
  ) => void;
  onMutate?: (variables: CreateAuditConfigParams) => void;
};

/**
 * Hook for creating an audit config from a source config.
 *
 * API Endpoint:
 * - POST /tenants/:tenantId/config/:configId/audit - Creates or retrieves an audit config
 *
 * @param config.onSuccess - Called after successful audit config creation
 * @param config.onError - Called if creation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation starts
 *
 * @returns React Query mutation result for creating audit config
 *
 * @example
 * ```tsx
 * const { mutateAsync, isPending } = useCreateAuditConfigMutation({
 *   onSuccess: (data) => console.log('Audit config created:', data.data.auditConfigId),
 *   onError: (error) => console.error('Failed to create audit config:', error)
 * });
 *
 * const result = await mutateAsync({ configId: 'config-123', revisionNumber: 1 });
 * ```
 */
export function useCreateAuditConfigMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: CreateAuditConfigMutationConfig = {}) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isPending, isError, error } = useMutation<
    CreateAuditConfigResponse2,
    Error,
    CreateAuditConfigParams
  >({
    mutationFn: async ({ configId, revisionNumber }: CreateAuditConfigParams) => {
      return mgmtPlaneAPIClient.nodeConfigs.createAuditConfig({
        tenantId: tenantId!,
        configId,
        requestBody: {
          revisionNumber,
        },
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, mutateAsync, isPending, isError, error };
}
